// src/components/form/DynamicUserForm.js
/**
 * DynamicUserForm
 * ---------------
 * Multi-step questionnaire.
 *
 * Responsibilities
 * • Filters the global `questions` array against current answers + user data.
 * • Generates a step list (`single`, `group`, `review`) via `buildSteps()`.
 * • Performs per-step validation before allowing Next.
 * • On the final “Confirm” step:
 *      – uploads answers as JSON to Cloud Storage → `user_metadata/{uid}/…`
 *      – falls back to `localStorage` when offline or on upload error.
 * • Redirects to the requested recording path once data is safely persisted.
 *
 * Notes
 * • Pure UI logic is delegated to smaller components/helpers so this file
 *   stays focused on orchestration.
 * • Hook calls are grouped by purpose: derived state → effects → handlers.
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import LinearProgress from "@mui/material/LinearProgress";
import { getAuth } from "firebase/auth";

import { useAuth } from "../../context/AuthContext";
import { firebaseApp } from "../../firebase/firebaseApp";

import QuestionStep from "./QuestionStep";
import GroupedQuestionStep from "./GroupedQuestionStep";
import ReviewStep from "./ReviewStep";

import { questions } from "../../utils/form/formQuestions";
import { isQuestionValid } from "../../utils/form/isQuestionValid";
import {
  buildSteps,
  filterQuestions,
  rangeDefaults,
} from "../../utils/form/formRuntime";
import { getPrimaryColour } from "../../utils/getPrimaryColour";
import { uploadMetadata } from "../../utils/firebase/uploadMetadata";
import FormNavButtons from "./FormNavButtons";
import { useTranslation } from "react-i18next";
import { useProfilePrefetch } from "../../context/ProfilePrefetchContext";
import ErrorPopup from "../ErrorPopup";
import { validateClinicAssociation } from "../../utils/recordingMode/validateClinicAssociation";
import { clinicAssocErrorKey } from "../../utils/recordingMode/clinicAssocErrorKey";
/* ------------------------------------------------------------------ */
/* Main component                                                      */
/* ------------------------------------------------------------------ */
/**
 * @param {{ initialData?: Record<string, any>, returnTo?: string }} props
 */
function DynamicUserForm({ initialData = {}, returnTo = "/" }) {
  const { t } = useTranslation(); // <<< point to the “form” namespace

  const { user } = useAuth();
  const navigate = useNavigate();

  /* ----------------------------- state ----------------------------- */

  const [popup, setPopup] = useState(null);
  const closePopup = useCallback(() => setPopup(null), []);

  // ---------- initial answers ----------
  const [formData, setFormData] = useState(initialData);
  const [currentStep, setCurrentStep] = useState(0);

  /* --------------------------- selectors --------------------------- */
  const filtered = useMemo(
    () => filterQuestions(questions, formData, user),
    [formData, user],
  );
  const steps = useMemo(() => buildSteps(filtered), [filtered]);
  const totalSteps = steps.length;
  const progress = (currentStep / totalSteps) * 100;

  const { setProfile } = useProfilePrefetch();

  /* ------------------- initialise range defaults ------------------ */
  useEffect(() => {
    const defaults = rangeDefaults(filtered, formData);
    if (Object.keys(defaults).length) {
      setFormData((p) => ({ ...p, ...defaults }));
    }
  }, [filtered, formData]);

  /* --------------------------- handlers --------------------------- */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleNext = useCallback(
    /** @param {React.FormEvent | undefined} e */
    async (e) => {
      e?.preventDefault();

      /* ── Final step → persist answers ───────────────────────────── */
      if (currentStep === totalSteps - 1) {
        try {
          const { currentUser } = getAuth(firebaseApp);
          if (!currentUser) throw new Error("No authenticated user");

          const subjectId = String(formData.id ?? "").trim();
          const clinicStudyId = String(formData.clinicStudyId ?? "").trim();

          // Enforce clinic association ONLY if clinicStudyId is present
          if (clinicStudyId) {
            const res = await validateClinicAssociation({
              clinicStudyId,
              subjectId,
            });

            if (!res.ok) {
              const msgKey = clinicAssocErrorKey(res.reason);

              setPopup({
                message:
                  res.reason === "not_found"
                    ? t(msgKey, { clinic: clinicStudyId })
                    : t(msgKey),
              });

              return; // block submit
            }
          }

          // Valid → persist
          await uploadMetadata(currentUser.uid, formData);
          setProfile(currentUser.uid, formData);

          try {
            sessionStorage.removeItem("formMetadata");
          } catch {}

          navigate(returnTo, { replace: true });
          return;
        } catch (err) {
          console.error("Metadata upload failed:", err);
          if (user?.uid) setProfile(user.uid, formData);
          return;
        }
      }

      /* ── Validate current step before advancing ─────────────────── */
      const step = steps[currentStep];
      const stepIsValid =
        step.type === "single"
          ? isQuestionValid(step.question, formData)
          : step.type === "group"
            ? !step.questions.some((q) => !isQuestionValid(q, formData))
            : true;

      if (!stepIsValid) {
        setPopup({
          message: t("form.reqFieldsAlert"),
        });
        return;
      }

      setCurrentStep((s) => s + 1);
    },
    [
      currentStep,
      totalSteps,
      formData,
      navigate,
      setProfile,
      user?.uid,
      t,
      steps,
      returnTo,
    ],
  );

  const handlePrev = (e) => {
    e.preventDefault();
    setCurrentStep((s) => Math.max(0, s - 1));
  };

  /* ------------- auto-fill when returning user -------------------- */
  useEffect(() => {
    if (user.returning) {
      const stored = sessionStorage.getItem("autoFillData");
      if (stored) setFormData((p) => ({ ...p, ...JSON.parse(stored) }));
    }
  }, [user.returning]);

  /* ---- edge case: zero questions after filtering → skip form ----- */
  useEffect(() => {
    if (!filtered.length) {
      navigate(returnTo);
    }
  }, [filtered, formData, navigate, returnTo]);

  const translationForm = "form";

  /* ------------------------ render helpers ------------------------ */
  const renderStep = () => {
    const step = steps[currentStep];
    switch (step.type) {
      case "single":
        return (
          <QuestionStep
            question={step.question}
            value={formData[step.question.id]}
            onChange={handleChange}
            autoFocus
            onEnter={handleNext}
            translationForm={translationForm}
          />
        );
      case "group":
        return (
          <GroupedQuestionStep
            questions={step.questions}
            formData={formData}
            onChange={handleChange}
            onEnter={handleNext}
            translationForm={translationForm}
          />
        );
      default:
        return (
          <ReviewStep
            questions={filtered}
            formData={formData}
            translationForm={translationForm}
          />
        );
    }
  };

  /* ------------------------------ UI ------------------------------ */
  return (
    <div className="user-form p-4 w-full mx-auto flex flex-col justify-between gap-4">
      <ErrorPopup popup={popup} onClose={closePopup} />
      {/* progress bar */}
      <LinearProgress
        variant="determinate"
        value={progress}
        className="rounded-full"
        sx={{
          backgroundColor: "#B776B9",
          "& .MuiLinearProgress-bar": { backgroundColor: getPrimaryColour() },
        }}
      />

      {renderStep()}

      {/* nav buttons */}
      <FormNavButtons
        hasPrev={currentStep > 0}
        onPrev={handlePrev}
        onNext={handleNext}
        isLast={currentStep === totalSteps - 1}
      />
    </div>
  );
}

export default DynamicUserForm;
